/**
 * LE FILIGRANE DE LA CONSOLE — réglable, et désactivable.
 *
 * Le fondateur, 15 septembre 2026 : « le filigrane de symbole est là par
 * défaut, même pas moyen de régler l'opacité ou de le changer ».
 *
 * ─── Pourquoi trois crans, et pas un curseur ────────────────────────────────
 *
 * L'opacité du motif est CUITE dans l'image SVG du jeton `--tex-bg` (voir le
 * commentaire de `.tex-bg` : une opacité CSS posée sur le conteneur rendrait
 * aussi le texte et les cartes translucides). Un curseur continu promettrait
 * donc un réglage que le motif ne sait pas rendre. Trois crans disent la
 * vérité — et « Aucun » est un vrai choix, pas une opacité proche de zéro.
 *
 * L'opacité vit désormais sur un PSEUDO-ÉLÉMENT (`.tex-bg::before`) : la
 * texture y est seule, le contenu n'en hérite pas, et le réglage peut enfin
 * l'atténuer sans toucher à la lisibilité.
 *
 * ─── Même mécanisme que l'apparence, à dessein ──────────────────────────────
 *
 * Clé versionnée, repli mémoire, attribut posé sur `<html>`, application AVANT
 * le premier rendu React. Deux mécanismes concurrents pour deux réglages
 * voisins finiraient par diverger — c'est la raison même pour laquelle
 * `apparence-console.ts` refuse de dupliquer sa palette.
 *
 * `localStorage`, pas le serveur : une préférence d'affichage est propre au
 * poste, comme le mode clair/sombre.
 */
export const NIVEAUX_FILIGRANE = ['plein', 'discret', 'aucun'] as const;
export type NiveauFiligrane = (typeof NIVEAUX_FILIGRANE)[number];

/** Clé de stockage, versionnée comme celle de l'apparence. */
export const CLE_FILIGRANE_CONSOLE = 'silex.console.filigrane.v1';

/** Défaut : `plein` — le rendu actuel, pour que personne ne voie sa console changer. */
export const FILIGRANE_PAR_DEFAUT: NiveauFiligrane = 'plein';

export function estNiveauFiligrane(valeur: unknown): valeur is NiveauFiligrane {
  return typeof valeur === 'string' && (NIVEAUX_FILIGRANE as readonly string[]).includes(valeur);
}

/** `localStorage` s'il est réellement utilisable, `null` sinon. Ne lève jamais. */
function stockage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    if (!s) return null;
    const sonde = `${CLE_FILIGRANE_CONSOLE}.sonde`;
    s.setItem(sonde, '1');
    s.removeItem(sonde);
    return s;
  } catch {
    return null;
  }
}

/** Repli mémoire : le réglage s'applique et ne survit pas au rechargement. */
let memoire: NiveauFiligrane | null = null;

/** Quand le stockage fonctionne, il fait autorité — même règle que l'apparence. */
export function lireFiligrane(): NiveauFiligrane {
  const s = stockage();
  if (s) {
    try {
      const lu = s.getItem(CLE_FILIGRANE_CONSOLE);
      return estNiveauFiligrane(lu) ? lu : FILIGRANE_PAR_DEFAUT;
    } catch {
      /* Stockage refusé en cours de route : on retombe sur la mémoire. */
    }
  }
  return memoire ?? FILIGRANE_PAR_DEFAUT;
}

export function ecrireFiligrane(niveau: NiveauFiligrane): void {
  memoire = niveau;
  try {
    stockage()?.setItem(CLE_FILIGRANE_CONSOLE, niveau);
  } catch {
    /* Quota ou stockage refusé : la mémoire tient le réglage. */
  }
}

/** Pose le niveau sur `<html>`. Le CSS n'a que trois cas à connaître. */
export function appliquerFiligrane(niveau: NiveauFiligrane): NiveauFiligrane {
  const racine = globalThis.document?.documentElement;
  if (racine) racine.setAttribute('data-filigrane', niveau);
  return niveau;
}

/** Appelé une fois depuis `main.tsx`, AVANT le premier rendu React. */
export function initialiserFiligrane(): NiveauFiligrane {
  return appliquerFiligrane(lireFiligrane());
}
